import Collection from '@/store/models/collections/Collection'
import CollectionItem from '@/store/models/collectionItems/CollectionItem'
import { normalizePublicCollectionItem, insertPublicItemPosters } from '@/utils/store/public-collection-item'

/**
 * Inserts one response's mixed items and sub-collections into the ORM. Shared
 * by the pinned reads of the member and public collection pages and by the tier
 * list Board, which all side-load the same two resource kinds off one payload.
 *
 * Public payloads carry relationship-named fields the ORM would mistake for
 * relations, so they go through `normalizePublicCollectionItem` and their
 * posters are inserted first.
 *
 * @param {Object} resources
 * @param {Array} [resources.items] - CollectionItem payloads
 * @param {Array} [resources.subCollections] - Collection payloads
 * @param {Object} options
 * @param {string} options.collectionId - The collection they were read from
 * @param {boolean} [options.isPublic=false] - Read through a /v1/public twin
 */
export async function insertMixedResources({ items = [], subCollections = [] }, { collectionId, isPublic = false }) {
  if (isPublic) await insertPublicItemPosters(items)

  const itemsToInsert = items.map(item => (
    isPublic
      ? normalizePublicCollectionItem(item, collectionId, { sortOrder: 0 })
      : { ...item, sortOrder: 0 }
  ))

  const subCollectionsToInsert = subCollections.map(subCollection => ({
    ...subCollection,
    ownerId: subCollection.owner?._id || subCollection.owner,
    ...(isPublic && { parent: collectionId, isPublic: true }),
    members: (subCollection.membersPreview || []).map(member => ({
      ...member,
      userId: member.user?._id,
      collectionId: subCollection._id,
    })),
  }))

  if (itemsToInsert.length) await CollectionItem.insertOrUpdate({ data: itemsToInsert })
  if (subCollectionsToInsert.length) await Collection.insertOrUpdate({ data: subCollectionsToInsert })
}
